// プレイバックルート（マーク列）の編集・新規作成のロジックを担うフック。
//
// 機体側には記録ルートを部分更新するコマンドが無いため、編集は「再記録」方式で行う:
// 1. getMap のステーション群からマーク列を復元（common/markUtil.ts）
// 2. ローカルで編集（移動 / 追加 / 削除 / 前進・後進 / 端点の荷役設定）
// 3. trajectory_command: clear → mark_at_poses → process で新ルートとして再生成
//    （新しいタイムスタンプのルート ID になる）
// 4. reload_map + getMap ポーリングで新ルート ID の出現を確認
// 5. 旧ルートを remove_recorded_map で削除し、旧ルートを参照するシナリオを
//    新ルート ID に書き換えて save_recorded_scenario で保存
//
// 新規作成は同じフローの 1〜4 のみ（マーク列を空から作り、旧ルート削除と
// シナリオ書き換えを行わない）。
// fork_webapp_react の usePlaybackRecord（登録フロー）と同じコマンド列を使う。

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { RefObject } from 'react'
import mqttService from '@/services/mqttService'
import type {
  EditableMark,
  PlaybackRoute,
  PlaybackScenario,
  RouteMap,
  ViewMode,
} from '@/types'
import { DIST_THRESHOLD } from '@/types'
import { extractRoutes } from '@/common/mapUtil'
import {
  alignEndpointToSlot,
  buildMarkAtPosesArgs,
  computeMarkThetas,
  extractEditableMarks,
  nearestSegmentIndex,
  rotateEndpointTo,
  validateMarks,
} from '@/common/markUtil'
import { MAX_PLAYBACK_ROUTES } from '@/config/scenarioConfig'

// 新ルート ID の出現を待つタイムアウト / ポーリング間隔（ミリ秒）
const REGISTER_TIMEOUT_MS = 15000
const REGISTER_POLL_INTERVAL_MS = 1000
// コマンドが順序どおりブローカーへ届くようにする待機（fork_webapp_react と同じ方式）
const COMMAND_GAP_MS = 300
const PROCESS_WAIT_MS = 500

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms))
}

// マーク列から新ルートを再記録し、新ルート ID（mapId）を返す。
// clear → mark_at_poses → process で再生成し、reload_map + getMap ポーリングで
// knownIds に無い新ルートの出現を確認する（タイムアウト時は null）。
// スロット一括移動（useScenarios）からも使うため関数として公開している。
export async function reRecordRoute(
  marks: EditableMark[],
  knownIds: Set<string>,
  currentMapId: string | null
): Promise<string | null> {
  // 記録器に残っているマークを消してから、全マークを送って再生成する
  mqttService.publishTrajectoryCommand('clear', [])
  await sleep(COMMAND_GAP_MS)
  mqttService.publishTrajectoryCommand(
    'mark_at_poses',
    buildMarkAtPosesArgs(marks)
  )
  await sleep(COMMAND_GAP_MS)
  mqttService.publishTrajectoryCommand('process', [])
  await sleep(PROCESS_WAIT_MS)

  // 新ルート ID（新しいタイムスタンプ）の出現をポーリングで確認
  const startTime = Date.now()
  while (Date.now() - startTime < REGISTER_TIMEOUT_MS) {
    if (currentMapId) mqttService.reloadMap(currentMapId)
    await sleep(REGISTER_POLL_INTERVAL_MS)
    try {
      const loaded = await mqttService.getMap()
      const found = extractRoutes(loaded).find((r) => !knownIds.has(r.mapId))
      if (found) return found.mapId
    } catch {
      // 応答タイムアウト等はリトライ
    }
  }
  return null
}

// シナリオ内の旧ルート参照（mapId / スロット ID）を新ルートに書き換える。
// マーク数が変わっても端点スロット名 (rec-1/rec-2) は不変なので文字列置換で安全。
export function rewriteScenarios(
  scenarios: PlaybackScenario[],
  oldMapId: string,
  newMapId: string
): { scenarios: PlaybackScenario[]; changed: boolean } {
  let changed = false
  const oldPrefix = `*r${oldMapId}/`
  const renameSlot = (slotId: string) =>
    slotId.replace(oldPrefix, `*r${newMapId}/`)
  // 停止手順（mapId=''）も停止位置として旧ルートの端点スロットを参照し得る
  const refersOld = (r: PlaybackRoute) =>
    r.mapId === oldMapId || r.slots.some((s) => s.startsWith(oldPrefix))
  const rewritten = scenarios.map((scenario) => {
    if (!scenario.routes.some(refersOld)) return scenario
    changed = true
    return {
      ...scenario,
      routes: scenario.routes.map((route) => {
        if (!refersOld(route)) return route
        if (route.mapId !== oldMapId) {
          // 停止手順: 停止位置スロットの ID だけ新ルートへ付け替える
          return {
            ...route,
            slots: route.slots.map(renameSlot),
            startSlot: route.startSlot
              ? renameSlot(route.startSlot)
              : route.startSlot,
            endSlot: route.endSlot ? renameSlot(route.endSlot) : route.endSlot,
          }
        }
        return {
          ...route,
          id: route.id.replace(oldMapId, newMapId),
          mapId: newMapId,
          slots: route.slots.map(renameSlot),
          startSlot: route.startSlot
            ? renameSlot(route.startSlot)
            : route.startSlot,
          endSlot: route.endSlot ? renameSlot(route.endSlot) : route.endSlot,
          // 形状が変わっているためキャッシュ済みポリラインは破棄する
          computedRoutePoints: null,
        }
      }),
    }
  })
  return { scenarios: rewritten, changed }
}

interface UseRouteEditDeps {
  map: RouteMap | null
  routes: PlaybackRoute[]
  scenarios: PlaybackScenario[]
  scenariosRef: RefObject<PlaybackScenario[]>
  saveToDevice: (scenarios: PlaybackScenario[]) => Promise<boolean>
  showNotice: (message: string) => void
  reloadMapData: () => Promise<void>
  setViewMode: (mode: ViewMode) => void
}

export function useRouteEdit({
  map,
  routes,
  scenarios,
  scenariosRef,
  saveToDevice,
  showNotice,
  reloadMapData,
  setViewMode,
}: UseRouteEditDeps) {
  const [editingRoute, setEditingRoute] = useState<PlaybackRoute | null>(null)
  // 新規作成モード（既存ルートを持たず、空のマーク列から作る）
  const [isCreatingRoute, setIsCreatingRoute] = useState(false)
  const [marks, setMarks] = useState<EditableMark[]>([])
  const [selectedMarkIndex, setSelectedMarkIndex] = useState<number | null>(
    null
  )
  // undo/redo 履歴（マーク列のスナップショット）
  const [past, setPast] = useState<EditableMark[][]>([])
  const [future, setFuture] = useState<EditableMark[][]>([])
  // マーク追加モード（マップクリックで最寄り区間に挿入）
  const [isAddingMark, setIsAddingMark] = useState(false)
  const [isSavingRoute, setIsSavingRoute] = useState(false)
  const [showSaveConfirm, setShowSaveConfirm] = useState(false)
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)

  const marksRef = useRef<EditableMark[]>([])
  useEffect(() => {
    marksRef.current = marks
  }, [marks])
  // ドラッグ開始時のスナップショット（ドラッグ終了時に履歴へ積む）
  const dragBaseRef = useRef<EditableMark[] | null>(null)

  // 機体切替などでマップが失われたら編集状態を破棄する
  // （viewMode は接続ライフサイクル側で 'list' に戻る）
  useEffect(() => {
    if (!map && (editingRoute || isCreatingRoute)) {
      setEditingRoute(null)
      setIsCreatingRoute(false)
      setMarks([])
      setPast([])
      setFuture([])
      setSelectedMarkIndex(null)
      setIsAddingMark(false)
      setShowSaveConfirm(false)
      setShowCancelConfirm(false)
    }
  }, [map, editingRoute, isCreatingRoute])

  // --- computed ---
  // 編集または新規作成中か（ルート編集モードの UI 表示条件）
  const isActive = editingRoute !== null || isCreatingRoute
  const markThetas = useMemo(() => computeMarkThetas(marks), [marks])
  const markErrors = useMemo(() => validateMarks(marks), [marks])
  const hasChanges = past.length > 0
  const canUndo = past.length > 0
  const canRedo = future.length > 0
  // 編集対象ルートを使用しているシナリオ数（保存確認ダイアログの注意表示用）
  const usedScenarioCount = useMemo(() => {
    if (!editingRoute) return 0
    return scenarios.filter((sc) =>
      sc.routes.some((r) => r.mapId === editingRoute.mapId)
    ).length
  }, [scenarios, editingRoute])
  const canSave =
    hasChanges && marks.length >= 2 && markErrors.length === 0 && !isSavingRoute
  // 向きを変更（回転）できるマーク = 選択中の端点マーク。
  // 中継マークの向きは区間方向そのものなので、マーク移動で調整する。
  // 1点のみの場合も仮の向き（draftTheta）として回転できる。
  const rotatableMarkIndex =
    selectedMarkIndex !== null &&
    marks.length >= 1 &&
    (selectedMarkIndex === 0 || selectedMarkIndex === marks.length - 1)
      ? selectedMarkIndex
      : null

  const commitMarks = useCallback((next: EditableMark[]) => {
    setPast((p) => [...p, marksRef.current])
    setFuture([])
    setMarks(next)
  }, [])

  // --- 編集の開始 / 終了 ---
  const startEdit = useCallback(
    (route: PlaybackRoute) => {
      if (!map) return
      const extracted = extractEditableMarks(map, route.mapId)
      if (!extracted) {
        showNotice('ルートのマークを読み取れませんでした')
        return
      }
      setEditingRoute(route)
      setIsCreatingRoute(false)
      setMarks(extracted)
      setPast([])
      setFuture([])
      setSelectedMarkIndex(null)
      setIsAddingMark(false)
      setViewMode('routeEdit')
    },
    [map, showNotice, setViewMode]
  )

  // 新規作成を開始する（空のマーク列 + マーク追加モードで始める）
  const startCreate = useCallback(() => {
    if (!map) return
    if (routes.length >= MAX_PLAYBACK_ROUTES) {
      showNotice(`ルートは最大${MAX_PLAYBACK_ROUTES}件まで作成できます`)
      return
    }
    setEditingRoute(null)
    setIsCreatingRoute(true)
    setMarks([])
    setPast([])
    setFuture([])
    setSelectedMarkIndex(null)
    setIsAddingMark(true)
    setViewMode('routeEdit')
  }, [map, routes.length, showNotice, setViewMode])

  const exitEdit = useCallback(() => {
    setEditingRoute(null)
    setIsCreatingRoute(false)
    setMarks([])
    setPast([])
    setFuture([])
    setSelectedMarkIndex(null)
    setIsAddingMark(false)
    setShowSaveConfirm(false)
    setShowCancelConfirm(false)
    setViewMode('list')
  }, [setViewMode])

  const requestCancel = useCallback(() => {
    if (isSavingRoute) return
    if (hasChanges) {
      setShowCancelConfirm(true)
    } else {
      exitEdit()
    }
  }, [isSavingRoute, hasChanges, exitEdit])

  const confirmCancel = useCallback(() => {
    setShowCancelConfirm(false)
    exitEdit()
  }, [exitEdit])

  const dismissCancel = useCallback(() => {
    setShowCancelConfirm(false)
  }, [])

  // --- undo / redo ---
  const handleUndo = useCallback(() => {
    if (past.length === 0) return
    const prev = past[past.length - 1]
    setPast(past.slice(0, -1))
    setFuture([...future, marks])
    setMarks(prev)
    setSelectedMarkIndex((i) => (i !== null && i < prev.length ? i : null))
  }, [past, future, marks])

  const handleRedo = useCallback(() => {
    if (future.length === 0) return
    const next = future[future.length - 1]
    setFuture(future.slice(0, -1))
    setPast([...past, marks])
    setMarks(next)
    setSelectedMarkIndex((i) => (i !== null && i < next.length ? i : null))
  }, [past, future, marks])

  // --- マーク操作 ---
  const selectMark = useCallback((index: number | null) => {
    setSelectedMarkIndex(index)
  }, [])

  const toggleAddMode = useCallback(() => {
    setIsAddingMark((v) => !v)
  }, [])

  // 指定座標に一致（DIST_THRESHOLD 以内）する既存スロットのステーション theta を
  // 返す（無ければ null）。吸着時の角度あわせと、「このマークはスロットに接続
  // 済みなので動かしてはいけない」の判定に使う。
  const slotThetaAt = useCallback(
    (p: { x: number; y: number }): number | null => {
      if (!map) return null
      for (const slot of map.slots.values()) {
        const st = map.stations.get(slot.stationId)
        if (!st?.pose) continue
        if (
          Math.abs(st.pose.x - p.x) + Math.abs(st.pose.y - p.y) <=
          DIST_THRESHOLD
        ) {
          return st.pose.theta ?? 0
        }
      }
      return null
    },
    [map]
  )

  // マップクリックでマークを追加する。
  // 新規作成中は末尾に追加（クリック順に経路を描く）、
  // 編集中は最寄り区間に挿入する（走行方向は区間から引き継ぐ）。
  // snap は既存スロットへの吸着情報。スロットと同一座標にすることで
  // シナリオの接続判定（座標一致）を通せるようにする。ID は機体側で
  // 新規採番されるため引き継がない（fork_webapp_react の「接続」と同じ方式）。
  const addMarkAt = useCallback(
    (
      pos: { x: number; y: number },
      snap?: {
        x: number
        y: number
        theta?: number
        isCargo?: boolean
        height?: number
      }
    ) => {
      const current = marksRef.current
      const p = snap ? { x: snap.x, y: snap.y } : pos
      // 荷役スロットへ吸着した場合は高さ保存設定ごと引き継ぐ
      const snapCargo = snap?.isCargo === true && (snap.height ?? -1) >= 0
      if (isCreatingRoute || current.length < 2) {
        // 終点の高さ保存マーク配置後はマーク追加不可
        // （fork_webapp_react の記録時と同じ。高さ保存は先頭 / 末尾のみ）
        if (current.length >= 2 && current[current.length - 1].isCargo) {
          showNotice('高さ保存マークの後ろにはマークを追加できません')
          return
        }
        const newMark: EditableMark = {
          x: p.x,
          y: p.y,
          // 荷役マークへの進入は後進の規約なので、2つ目以降の荷役吸着は後進にする
          reverse: snapCargo && current.length > 0,
          isCargo: snapCargo,
          height: snapCargo ? (snap!.height as number) : -1,
          // 1点目をスロットへ吸着した場合はスロットの向きを仮の向きにする
          // （1点のみの間の表示と、2点目配置時の出発方向あわせに使う）
          draftTheta:
            current.length === 0 && snap?.theta != null
              ? snap.theta
              : undefined,
        }
        let next = [...current, newMark]
        // 端点の向きもスロットにあわせる（隣のマークを端点中心に回転して
        // 区間方向を微調整する）。ずれが大きい場合（SNAP_ALIGN_MAX_DIFF 超）は
        // クリック位置を優先してあわせず、隣のマークが別のスロット上にある
        // 場合もその接続を壊さないよう回転しない。
        if (snap?.theta != null && next.length >= 2) {
          if (slotThetaAt(next[next.length - 2]) == null) {
            next =
              alignEndpointToSlot(next, next.length - 1, snap.theta) ?? next
          }
        } else if (!snap && next.length === 2) {
          // 先頭マークに仮の向き（回転ハンドル / スロット吸着で指定）があれば、
          // 出発方向をその向きへ微調整
          const startTheta = next[0].draftTheta ?? slotThetaAt(next[0])
          if (startTheta != null && slotThetaAt(next[1]) == null) {
            next = alignEndpointToSlot(next, 0, startTheta) ?? next
          }
        }
        commitMarks(next)
        setSelectedMarkIndex(current.length)
        // 追加モードは 1 点追加するごとに解除する（続けて追加するときは
        // 都度ツールバーの追加ボタンを押す。誤クリックでの連続追加を防ぐ）
        setIsAddingMark(false)
        return
      }
      const seg = nearestSegmentIndex(current, p)
      // 中間マークは荷役にできないため、吸着時も座標のみ引き継ぐ
      const newMark: EditableMark = {
        x: p.x,
        y: p.y,
        reverse: current[seg + 1].reverse,
        isCargo: false,
        height: -1,
      }
      commitMarks([
        ...current.slice(0, seg + 1),
        newMark,
        ...current.slice(seg + 1),
      ])
      setSelectedMarkIndex(seg + 1)
      setIsAddingMark(false)
    },
    [isCreatingRoute, commitMarks, showNotice, slotThetaAt]
  )

  const deleteMark = useCallback(
    (index: number) => {
      const current = marksRef.current
      // 既存ルートの編集では 2 マーク未満にできない（新規作成中は自由に削除可）
      if (!isCreatingRoute && current.length <= 2) {
        showNotice('マークは2つ以上必要です')
        return
      }
      commitMarks(current.filter((_, i) => i !== index))
      setSelectedMarkIndex(null)
    },
    [isCreatingRoute, commitMarks, showNotice]
  )

  const setMarkPosition = useCallback(
    (index: number, x: number, y: number) => {
      const current = marksRef.current
      if (!Number.isFinite(x) || !Number.isFinite(y)) return
      if (current[index].x === x && current[index].y === y) return
      commitMarks(current.map((m, i) => (i === index ? { ...m, x, y } : m)))
    },
    [commitMarks]
  )

  // このマークへ入る区間の前進/後進を切り替える（先頭マークは対象外）
  const toggleReverse = useCallback(
    (index: number) => {
      const current = marksRef.current
      if (index < 1 || index >= current.length) return
      commitMarks(
        current.map((m, i) => (i === index ? { ...m, reverse: !m.reverse } : m))
      )
    },
    [commitMarks]
  )

  // 端点マークの荷役（高さ保存）を切り替える（機体側で永続化されるのは端点のみ）
  const toggleCargo = useCallback(
    (index: number) => {
      const current = marksRef.current
      if (index !== 0 && index !== current.length - 1) return
      commitMarks(
        current.map((m, i) => {
          if (i !== index) return m
          const isCargo = !m.isCargo
          return { ...m, isCargo, height: isCargo ? Math.max(m.height, 0) : -1 }
        })
      )
    },
    [commitMarks]
  )

  const setMarkHeightMm = useCallback(
    (index: number, heightMm: number) => {
      const current = marksRef.current
      if (!Number.isFinite(heightMm) || heightMm < 0) return
      const height = heightMm / 1000
      if (current[index].height === height) return
      commitMarks(current.map((m, i) => (i === index ? { ...m, height } : m)))
    },
    [commitMarks]
  )

  // --- ドラッグ移動（移動中は履歴を積まず、終了時にまとめて1操作にする） ---
  // ドラッグ中のマークインデックスと、最後に吸着したスロットの theta
  // （ドラッグ終了時の角度あわせ用。吸着していなければ null）
  const dragIndexRef = useRef<number | null>(null)
  const dragSnapThetaRef = useRef<number | null>(null)

  const handleMarkDragStart = useCallback((index: number) => {
    dragBaseRef.current = marksRef.current
    dragIndexRef.current = index
    dragSnapThetaRef.current = null
    setSelectedMarkIndex(index)
  }, [])

  const handleMarkDrag = useCallback(
    (index: number, pos: { x: number; y: number }, snapTheta?: number) => {
      dragSnapThetaRef.current = snapTheta ?? null
      setMarks((prev) =>
        prev.map((m, i) => (i === index ? { ...m, x: pos.x, y: pos.y } : m))
      )
    },
    []
  )

  const handleMarkDragEnd = useCallback(() => {
    const base = dragBaseRef.current
    dragBaseRef.current = null
    const idx = dragIndexRef.current
    dragIndexRef.current = null
    const snapTheta = dragSnapThetaRef.current
    dragSnapThetaRef.current = null
    if (!base) return
    const current = marksRef.current
    const moved = base.some(
      (m, i) => m.x !== current[i]?.x || m.y !== current[i]?.y
    )
    if (!moved) return
    // 端点をスロットへ吸着させた場合は向きもスロットにあわせる（ずれが
    // 大きい場合はあわせない。隣のマークが別のスロット上にある場合も
    // 接続を保つため回転しない）
    if (
      snapTheta != null &&
      idx != null &&
      current.length >= 2 &&
      (idx === 0 || idx === current.length - 1)
    ) {
      const neighbor = current[idx === 0 ? 1 : current.length - 2]
      if (slotThetaAt(neighbor) == null) {
        const aligned = alignEndpointToSlot(current, idx, snapTheta)
        if (aligned) setMarks(aligned)
      }
    }
    setPast((p) => [...p, base])
    setFuture([])
  }, [slotThetaAt])

  // --- 端点マークの向き変更（隣のマークを端点中心に回転して区間方向を合わせる） ---
  const rotateBaseRef = useRef<EditableMark[] | null>(null)

  // 数値入力からの向き変更（1 操作としてコミットする）
  const setEndpointTheta = useCallback(
    (index: number, theta: number) => {
      const next = rotateEndpointTo(marksRef.current, index, theta)
      if (next) commitMarks(next)
    },
    [commitMarks]
  )

  // 回転ハンドルのドラッグ（回転中は履歴を積まず、終了時にまとめて 1 操作にする）
  const handleMarkRotateStart = useCallback((index: number) => {
    rotateBaseRef.current = marksRef.current
    setSelectedMarkIndex(index)
  }, [])

  const handleMarkRotate = useCallback((index: number, theta: number) => {
    const next = rotateEndpointTo(marksRef.current, index, theta)
    if (next) setMarks(next)
  }, [])

  const handleMarkRotateEnd = useCallback(() => {
    const base = rotateBaseRef.current
    rotateBaseRef.current = null
    if (!base) return
    const current = marksRef.current
    const moved = base.some(
      (m, i) =>
        m.x !== current[i]?.x ||
        m.y !== current[i]?.y ||
        m.draftTheta !== current[i]?.draftTheta
    )
    if (!moved) return
    setPast((p) => [...p, base])
    setFuture([])
  }, [])

  // --- 保存 ---
  const requestSave = useCallback(() => {
    if (!canSave) return
    setShowSaveConfirm(true)
  }, [canSave])

  const cancelSave = useCallback(() => {
    if (!isSavingRoute) setShowSaveConfirm(false)
  }, [isSavingRoute])

  const confirmSave = useCallback(async () => {
    const route = editingRoute
    if ((!route && !isCreatingRoute) || isSavingRoute) return
    setIsSavingRoute(true)
    try {
      const knownIds = new Set(routes.map((r) => r.mapId))
      const mapId = await mqttService.getMapId()

      // 編集後の全マークで再記録し、新ルート ID の出現を待つ
      const newRouteId = await reRecordRoute(marksRef.current, knownIds, mapId)

      if (!newRouteId) {
        showNotice(
          isCreatingRoute
            ? 'ルートの登録に失敗しました（タイムアウト）'
            : 'ルートの保存に失敗しました（タイムアウト）'
        )
        return
      }

      // 既存ルートの編集: 旧ルートを削除し、参照しているシナリオを書き換える
      // （新規作成では不要）
      if (route) {
        const oldRouteId = route.mapId
        // 旧ルートを削除（機体側でマップ再読み込みを伴う）
        mqttService.removeRecordedMap(oldRouteId)

        // 旧ルートを参照しているシナリオを新ルート ID へ書き換えて保存
        const { scenarios: rewritten, changed } = rewriteScenarios(
          scenariosRef.current ?? [],
          oldRouteId,
          newRouteId
        )
        if (changed) {
          const ok = await saveToDevice(rewritten)
          if (!ok) {
            showNotice(
              'シナリオの参照更新に失敗しました。シナリオを確認してください'
            )
          }
        }
        await sleep(PROCESS_WAIT_MS)
      }

      await reloadMapData()
      showNotice(
        isCreatingRoute ? 'ルートを登録しました' : 'ルートを保存しました'
      )
      exitEdit()
    } finally {
      setIsSavingRoute(false)
      setShowSaveConfirm(false)
    }
  }, [
    editingRoute,
    isCreatingRoute,
    isSavingRoute,
    routes,
    scenariosRef,
    saveToDevice,
    showNotice,
    reloadMapData,
    exitEdit,
  ])

  return {
    editingRoute,
    isCreatingRoute,
    isActive,
    marks,
    markThetas,
    markErrors,
    selectedMarkIndex,
    isAddingMark,
    isSavingRoute,
    hasChanges,
    canUndo,
    canRedo,
    canSave,
    rotatableMarkIndex,
    usedScenarioCount,
    showSaveConfirm,
    showCancelConfirm,

    startEdit,
    startCreate,
    requestCancel,
    confirmCancel,
    dismissCancel,
    handleUndo,
    handleRedo,
    selectMark,
    toggleAddMode,
    addMarkAt,
    deleteMark,
    setMarkPosition,
    toggleReverse,
    toggleCargo,
    setMarkHeightMm,
    handleMarkDragStart,
    handleMarkDrag,
    handleMarkDragEnd,
    setEndpointTheta,
    handleMarkRotateStart,
    handleMarkRotate,
    handleMarkRotateEnd,
    requestSave,
    cancelSave,
    confirmSave,
  }
}

export type RouteEdit = ReturnType<typeof useRouteEdit>
